import apiClient from "../../client/apiClient"

export type BulkPromotePayload = {
  enrollmentIds: string[]
  targetSessionId: string
  targetClassId: string
  // Section NAME (not id) — the backend re-creates it for the target session.
  targetSectionName: string
  targetGroupId?: string
  // Set true to actually apply a sideways update after the caller has already
  // confirmed a CONFIRM_ALREADY_PROMOTED response — otherwise the backend
  // asks first and applies nothing.
  confirmUpdate?: boolean
}

export type BackwardPromotionDetail = {
  enrollmentId: string
  name: string
  grNumber: string
}

export type AlreadyPromotedDetail = BackwardPromotionDetail & {
  // Where the student is already enrolled in the target session — group is
  // only present when the target class actually has groups.
  targetClass?: string
  targetSection?: string
  targetGroup?: string | null
}

export type BulkPromoteResult = {
  success: boolean
  message: string
  promoted?: number
  // Existing target-session enrollments whose class/section/group were
  // corrected in place (a "sideways" move — same or later session/class).
  updated?: number
  unchanged?: number
  // Some/all selected students are already enrolled in the target session —
  // nothing was written yet. Re-call promoteBulk with confirmUpdate:true
  // (same payload) once the user confirms, to actually apply the update.
  code?: "CONFIRM_ALREADY_PROMOTED"
  details?: AlreadyPromotedDetail[]
}

// Promote the selected students into the target session. Re-promoting a
// student already in the target session ASKS first (code
// CONFIRM_ALREADY_PROMOTED, nothing written) then UPDATES their class/
// section/group in place instead of creating a duplicate once confirmed. A
// 400 with code BACKWARD_PROMOTION means the target is an earlier session/
// class than the student's current one — that's rejected outright, not
// something to retry.
export const promoteBulk = async (payload: BulkPromotePayload): Promise<BulkPromoteResult> => {
  const res = await apiClient({ method: "POST", url: "/promotion/bulk", data: payload })
  return res.data
}
